/**
 * Particle and decal effects: tyre smoke, off-road dust, boost flame, impact
 * sparks and the rubber a sliding kart leaves on the tarmac.
 *
 * Everything is pooled and drawn in three passes total. The two particle pools
 * are single THREE.Points objects over a fixed ring buffer — one alpha-blended
 * (smoke, dust) and one additive (flame, sparks) — driven by a small custom
 * shader, because PointsMaterial cannot fade individual particles. Skid marks
 * are a ring buffer of quads whose age lives in a vertex attribute, so they fade
 * out on the GPU without any per-frame CPU work.
 *
 * Effects are purely cosmetic: nothing here feeds back into the simulation, so
 * it is always safe to skip or reset.
 */
import * as THREE from 'three';

import { createPuffTexture } from './textures';

const PARTICLE_VERT = /* glsl */ `
attribute float size;
attribute float alpha;
attribute vec3 tint;
varying float vAlpha;
varying vec3 vTint;

void main() {
  vAlpha = alpha;
  vTint = tint;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = size * (320.0 / max(-mv.z, 1.0));
  gl_Position = projectionMatrix * mv;
}
`;

const PARTICLE_FRAG = /* glsl */ `
uniform sampler2D uMap;
varying float vAlpha;
varying vec3 vTint;

void main() {
  float a = texture2D(uMap, gl_PointCoord).a * vAlpha;
  if (a < 0.01) discard;
  gl_FragColor = vec4(vTint, a);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const SKID_VERT = /* glsl */ `
attribute float birth;
uniform float uTime;
uniform float uLife;
varying float vFade;
varying vec2 vUv;

void main() {
  vFade = clamp(1.0 - (uTime - birth) / uLife, 0.0, 1.0);
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const SKID_FRAG = /* glsl */ `
uniform vec3 uColor;
uniform float uOpacity;
varying float vFade;
varying vec2 vUv;

void main() {
  // Soften both long edges so the rubber has no hard border.
  float edge = smoothstep(0.0, 0.3, vUv.x) * smoothstep(1.0, 0.7, vUv.x);
  float a = vFade * uOpacity * edge;
  if (a < 0.01) discard;
  gl_FragColor = vec4(uColor, a);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** One spawn request. Everything is world-space. */
interface SpawnOptions {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  color: THREE.Color;
  size: number;
  /** Units added to the point size per second of life. */
  growth: number;
  /** Vertical acceleration: positive for buoyant smoke, negative for sparks. */
  gravity: number;
  /** Fraction of velocity shed per second. */
  drag: number;
  life: number;
  alpha: number;
}

/**
 * A fixed-size pool of point particles. New spawns overwrite the oldest slot, so
 * there is no allocation after construction and no way to run out.
 */
class ParticlePool {
  readonly points: THREE.Points;

  private readonly position: Float32Array;
  private readonly size: Float32Array;
  private readonly alpha: Float32Array;
  private readonly tint: Float32Array;

  // Simulation state, parallel to the attribute arrays.
  private readonly velocity: Float32Array;
  private readonly growth: Float32Array;
  private readonly gravity: Float32Array;
  private readonly drag: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly peakAlpha: Float32Array;

  private cursor = 0;
  private live = 0;

  constructor(
    private readonly capacity: number,
    map: THREE.Texture,
    blending: THREE.Blending,
  ) {
    this.position = new Float32Array(capacity * 3);
    this.velocity = new Float32Array(capacity * 3);
    this.tint = new Float32Array(capacity * 3);
    this.size = new Float32Array(capacity);
    this.alpha = new Float32Array(capacity);
    this.growth = new Float32Array(capacity);
    this.gravity = new Float32Array(capacity);
    this.drag = new Float32Array(capacity);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity);
    this.peakAlpha = new Float32Array(capacity);

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.position, 3));
    geo.setAttribute('tint', new THREE.BufferAttribute(this.tint, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(this.size, 1));
    geo.setAttribute('alpha', new THREE.BufferAttribute(this.alpha, 1));
    // Particles are scattered all over the track; skip the culling test.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

    this.points = new THREE.Points(
      geo,
      new THREE.ShaderMaterial({
        vertexShader: PARTICLE_VERT,
        fragmentShader: PARTICLE_FRAG,
        uniforms: { uMap: { value: map } },
        transparent: true,
        depthWrite: false,
        blending,
      }),
    );
    this.points.frustumCulled = false;
    this.points.renderOrder = 5;
  }

  spawn(o: SpawnOptions): void {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;
    if (this.live < this.capacity) this.live++;

    const i3 = i * 3;
    this.position[i3] = o.x;
    this.position[i3 + 1] = o.y;
    this.position[i3 + 2] = o.z;
    this.velocity[i3] = o.vx;
    this.velocity[i3 + 1] = o.vy;
    this.velocity[i3 + 2] = o.vz;
    this.tint[i3] = o.color.r;
    this.tint[i3 + 1] = o.color.g;
    this.tint[i3 + 2] = o.color.b;

    this.size[i] = o.size;
    this.growth[i] = o.growth;
    this.gravity[i] = o.gravity;
    this.drag[i] = o.drag;
    this.life[i] = o.life;
    this.maxLife[i] = o.life;
    this.peakAlpha[i] = o.alpha;
    this.alpha[i] = o.alpha;
  }

  update(dt: number): void {
    if (this.live === 0) return;
    for (let i = 0; i < this.live; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this.alpha[i] = 0;
        continue;
      }

      const i3 = i * 3;
      const decay = Math.max(0, 1 - this.drag[i] * dt);
      this.velocity[i3] *= decay;
      this.velocity[i3 + 1] = this.velocity[i3 + 1] * decay + this.gravity[i] * dt;
      this.velocity[i3 + 2] *= decay;

      this.position[i3] += this.velocity[i3] * dt;
      this.position[i3 + 1] += this.velocity[i3 + 1] * dt;
      this.position[i3 + 2] += this.velocity[i3 + 2] * dt;

      this.size[i] += this.growth[i] * dt;
      // Ease out over the back half of the lifetime.
      const t = this.life[i] / this.maxLife[i];
      this.alpha[i] = this.peakAlpha[i] * Math.min(1, t * 2) ** 1.4;
    }

    const geo = this.points.geometry;
    geo.attributes.position.needsUpdate = true;
    geo.attributes.size.needsUpdate = true;
    geo.attributes.alpha.needsUpdate = true;
    geo.attributes.tint.needsUpdate = true;
  }

  clear(): void {
    this.alpha.fill(0);
    this.life.fill(0);
    this.live = 0;
    this.cursor = 0;
    this.points.geometry.attributes.alpha.needsUpdate = true;
  }

  dispose(): void {
    this.points.geometry.dispose();
    (this.points.material as THREE.Material).dispose();
  }
}

/**
 * Rubber laid down by sliding tyres. Segments are quads in a ring buffer; each
 * vertex carries the time it was written, and the shader fades it from there —
 * so a full track of marks costs one draw call and zero per-frame CPU.
 */
class SkidTrails {
  readonly mesh: THREE.Mesh;

  private readonly position: Float32Array;
  private readonly birth: Float32Array;
  private readonly material: THREE.ShaderMaterial;
  private readonly capacity: number;
  private cursor = 0;

  /** Where each emitter's previous mark ended, so segments join up. */
  private readonly lastPoint = new Map<string, THREE.Vector3>();
  private readonly scratch = new THREE.Vector3();

  constructor(capacity = 520, life = 14) {
    this.capacity = capacity;
    this.position = new Float32Array(capacity * 4 * 3);
    this.birth = new Float32Array(capacity * 4);
    const uv = new Float32Array(capacity * 4 * 2);
    const index = new Uint16Array(capacity * 6);

    for (let q = 0; q < capacity; q++) {
      const v = q * 4;
      // u runs across the strip so the fragment shader can soften both edges.
      uv[v * 2] = 0; uv[v * 2 + 1] = 0;
      uv[(v + 1) * 2] = 1; uv[(v + 1) * 2 + 1] = 0;
      uv[(v + 2) * 2] = 0; uv[(v + 2) * 2 + 1] = 1;
      uv[(v + 3) * 2] = 1; uv[(v + 3) * 2 + 1] = 1;
      const t = q * 6;
      index[t] = v; index[t + 1] = v + 2; index[t + 2] = v + 1;
      index[t + 3] = v + 1; index[t + 4] = v + 2; index[t + 5] = v + 3;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.position, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setAttribute('birth', new THREE.BufferAttribute(this.birth, 1));
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);

    this.material = new THREE.ShaderMaterial({
      vertexShader: SKID_VERT,
      fragmentShader: SKID_FRAG,
      uniforms: {
        uTime: { value: 0 },
        uLife: { value: life },
        uColor: { value: new THREE.Color(0x0d0d10) },
        uOpacity: { value: 0.55 },
      },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Marks sit a few millimetres over the tarmac; nudge them toward the
      // camera as well so they never shimmer against the road at distance.
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -8,
    });

    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    // Nothing is written yet — hide the degenerate quads until the first mark.
    geo.setDrawRange(0, 0);
  }

  /**
   * Continue `key`'s trail up to `point`. `right` is the lateral axis of the
   * tyre; `width` the mark's width. Long jumps (respawn, teleport, a new race)
   * restart the trail instead of drawing a streak across the map.
   */
  extend(
    key: string,
    point: THREE.Vector3,
    right: THREE.Vector3,
    width: number,
    time: number,
  ): void {
    const previous = this.lastPoint.get(key);
    if (!previous) {
      this.lastPoint.set(key, point.clone());
      return;
    }

    const step = this.scratch.copy(point).sub(previous).length();
    if (step < 0.22) return; // too small to be worth a quad
    if (step > 8) {
      previous.copy(point);
      return;
    }

    const q = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;
    const v = q * 12;
    const hx = right.x * width * 0.5;
    const hz = right.z * width * 0.5;

    this.position[v] = previous.x - hx;
    this.position[v + 1] = previous.y;
    this.position[v + 2] = previous.z - hz;
    this.position[v + 3] = previous.x + hx;
    this.position[v + 4] = previous.y;
    this.position[v + 5] = previous.z + hz;
    this.position[v + 6] = point.x - hx;
    this.position[v + 7] = point.y;
    this.position[v + 8] = point.z - hz;
    this.position[v + 9] = point.x + hx;
    this.position[v + 10] = point.y;
    this.position[v + 11] = point.z + hz;

    const b = q * 4;
    this.birth[b] = time;
    this.birth[b + 1] = time;
    this.birth[b + 2] = time;
    this.birth[b + 3] = time;

    previous.copy(point);

    const geo = this.mesh.geometry;
    geo.attributes.position.needsUpdate = true;
    geo.attributes.birth.needsUpdate = true;
    geo.setDrawRange(0, this.capacity * 6);
  }

  /** Break every trail without erasing the marks already on the ground. */
  breakTrails(): void {
    this.lastPoint.clear();
  }

  setTime(time: number): void {
    this.material.uniforms.uTime.value = time;
  }

  clear(): void {
    this.position.fill(0);
    this.birth.fill(-1e6); // fully aged out
    this.cursor = 0;
    this.lastPoint.clear();
    const geo = this.mesh.geometry;
    geo.attributes.position.needsUpdate = true;
    geo.attributes.birth.needsUpdate = true;
    geo.setDrawRange(0, 0);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

const _color = new THREE.Color();

/** The whole effects layer. One instance owns the pools and adds them to the scene. */
export class Effects {
  private readonly soft: ParticlePool;
  private readonly additive: ParticlePool;
  private readonly skids: SkidTrails;
  private readonly puff: THREE.Texture;

  /** Seconds since the effects layer started; drives the skid-mark fade. */
  private elapsed = 0;

  /**
   * Fraction of the full particle budget to spend, from the quality preset.
   * Below 1 emissions are dropped at random, which thins the plume without
   * changing how any single puff looks.
   */
  private budget = 1;

  constructor(scene: THREE.Scene) {
    this.puff = createPuffTexture();
    this.soft = new ParticlePool(420, this.puff, THREE.NormalBlending);
    this.additive = new ParticlePool(300, this.puff, THREE.AdditiveBlending);
    this.skids = new SkidTrails();
    scene.add(this.skids.mesh, this.soft.points, this.additive.points);
  }

  /** Set the share of the particle budget to spend, 0..1 (quality preset). */
  setBudget(scale: number): void {
    this.budget = Math.max(0, Math.min(1, scale));
  }

  /** Should this emission be skipped to stay inside the budget? */
  private skip(): boolean {
    return this.budget < 1 && Math.random() > this.budget;
  }

  /**
   * Tyre smoke: a slow, buoyant, spreading puff. `intensity` (0..1) scales how
   * much comes off, so a gentle slide barely smokes and a full lock-up billows.
   */
  tyreSmoke(x: number, y: number, z: number, intensity: number): void {
    if (this.skip()) return;
    const count = intensity > 0.65 ? 2 : 1;
    for (let i = 0; i < count; i++) {
      this.soft.spawn({
        x: x + (Math.random() - 0.5) * 0.3,
        y: y + 0.06,
        z: z + (Math.random() - 0.5) * 0.3,
        vx: (Math.random() - 0.5) * 2.2,
        vy: 0.7 + Math.random() * 1.1,
        vz: (Math.random() - 0.5) * 2.2,
        color: _color.setHex(0xd7d9de).clone(),
        size: 0.7 + Math.random() * 0.5,
        growth: 3.4,
        gravity: 1.1,
        drag: 1.7,
        life: 0.75 + Math.random() * 0.5,
        alpha: 0.16 + intensity * 0.3,
      });
    }
  }

  /** Dirt thrown up off the racing surface, tinted to the track's terrain. */
  dust(x: number, y: number, z: number, color: number, intensity: number): void {
    if (this.skip()) return;
    this.soft.spawn({
      x: x + (Math.random() - 0.5) * 0.6,
      y: y + 0.05,
      z: z + (Math.random() - 0.5) * 0.6,
      vx: (Math.random() - 0.5) * 3.4,
      vy: 1.2 + Math.random() * 2.0,
      vz: (Math.random() - 0.5) * 3.4,
      color: _color.setHex(color).clone(),
      size: 0.9 + Math.random() * 0.8,
      growth: 4.2,
      gravity: -0.6,
      drag: 1.4,
      life: 0.8 + Math.random() * 0.6,
      alpha: 0.22 + intensity * 0.4,
    });
  }

  /**
   * Boost afterburner. `dx/dy/dz` is the exhaust direction (unit-ish); flames
   * are fired along it and die fast, so the plume tracks the kart tightly.
   */
  flame(
    x: number, y: number, z: number,
    dx: number, dy: number, dz: number,
    intensity: number,
  ): void {
    if (this.skip()) return;
    const hot = Math.random() < 0.4;
    this.additive.spawn({
      x, y, z,
      vx: dx * 9 + (Math.random() - 0.5) * 2.4,
      vy: dy * 9 + Math.random() * 1.2,
      vz: dz * 9 + (Math.random() - 0.5) * 2.4,
      color: _color.setHex(hot ? 0xfff0b8 : 0xff7a1f).clone(),
      size: 0.5 + Math.random() * 0.4,
      growth: hot ? 1.4 : 3.2,
      gravity: 2.4,
      drag: 5.5,
      life: 0.16 + Math.random() * 0.16,
      alpha: 0.5 * intensity,
    });
  }

  /** Sparks off a barrier scrape or a kart-to-kart hit. */
  sparks(x: number, y: number, z: number, nx: number, nz: number, intensity: number): void {
    const count = Math.max(1, Math.round(Math.min(14, 3 + intensity * 11) * this.budget));
    for (let i = 0; i < count; i++) {
      this.additive.spawn({
        x, y: y + 0.25, z,
        vx: nx * 5 + (Math.random() - 0.5) * 9,
        vy: 2.5 + Math.random() * 5,
        vz: nz * 5 + (Math.random() - 0.5) * 9,
        color: _color.setHex(Math.random() < 0.5 ? 0xffe6a0 : 0xff9a2a).clone(),
        size: 0.22 + Math.random() * 0.2,
        growth: -0.1,
        gravity: -16,
        drag: 0.5,
        life: 0.3 + Math.random() * 0.4,
        alpha: 0.9,
      });
    }
  }

  /** Continue a tyre's rubber trail. See SkidTrails.extend. */
  skid(key: string, point: THREE.Vector3, right: THREE.Vector3, width: number): void {
    this.skids.extend(key, point, right, width, this.elapsed);
  }

  /** Called when a kart stops sliding, so the next mark starts a fresh streak. */
  endSkid(): void {
    this.skids.breakTrails();
  }

  update(dt: number): void {
    this.elapsed += dt;
    this.skids.setTime(this.elapsed);
    this.soft.update(dt);
    this.additive.update(dt);
  }

  /** Wipe every particle and mark — used between races and on track changes. */
  reset(): void {
    this.soft.clear();
    this.additive.clear();
    this.skids.clear();
  }

  dispose(): void {
    this.soft.dispose();
    this.additive.dispose();
    this.skids.dispose();
    this.puff.dispose();
  }
}
